import {
  User,
  BlogPost,
  Comment,
  Like,
  Testimonial,
  CustomRole,
} from "./types";
import { INITIAL_USERS, BLOG_POSTS, INITIAL_TESTIMONIALS } from "./constants";

import axios from "axios";

type ReplaceAllResult = { ok: boolean; count: number };

// Create axios instance with base URL
const client = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "",
  headers: {
    "Content-Type": "application/json",
  },
});

async function api<T>(path: string, init?: any): Promise<T> {
  try {
    const response = await client({
      url: path,
      method: init?.method || "GET",
      data: init?.body ? JSON.parse(init.body) : undefined,
      headers: init?.headers,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      `API Error: ${error.response?.status} ${
        error.response?.statusText || error.message
      }`
    );
  }
}

async function getAll<T>(collection: string): Promise<T[]> {
  return api<T[]>(`/api/${collection}`);
}

async function replaceAll<T>(
  collection: string,
  items: T[]
): Promise<ReplaceAllResult> {
  return api<ReplaceAllResult>(`/api/${collection}`, {
    method: "PUT",
    body: JSON.stringify({ items }),
  });
}

class MongoBackedDB {
  // Initialization & Seeding (idempotent)
  async initialize() {
    await api("/api/seed", {
      method: "POST",
      body: JSON.stringify({
        users: INITIAL_USERS,
        posts: BLOG_POSTS,
        testimonials: INITIAL_TESTIMONIALS,
      }),
    });
    return true;
  }

  async getDiagnostics() {
    return api<any>("/api/diagnostics");
  }

  async generateMigrationScript() {
    const res = await api<{ sql: string }>("/api/maintenance/migration-script");
    return res.sql;
  }

  users = {
    getAll: async (): Promise<User[]> => getAll<User>("users"),
    saveAll: async (users: User[]) => {
      await replaceAll<User>("users", users);
    },
    findByEmail: async (email: string): Promise<User | undefined> => {
      const user = await api<User | null>(
        `/api/users/by-email?email=${encodeURIComponent(email)}`
      );
      return user || undefined;
    },
  };

  posts = {
    getAll: async (): Promise<BlogPost[]> => getAll<BlogPost>("posts"),
    saveAll: async (posts: BlogPost[]) => {
      await replaceAll<BlogPost>("posts", posts);
    },
  };

  comments = {
    getAll: async (): Promise<Comment[]> => getAll<Comment>("comments"),
    saveAll: async (comments: Comment[]) => {
      await replaceAll<Comment>("comments", comments);
    },
  };

  likes = {
    getAll: async (): Promise<Like[]> => getAll<Like>("likes"),
    saveAll: async (likes: Like[]) => {
      await replaceAll<Like>("likes", likes);
    },
  };

  testimonials = {
    getAll: async (): Promise<Testimonial[]> =>
      getAll<Testimonial>("testimonials"),
    saveAll: async (t: Testimonial[]) => {
      await replaceAll<Testimonial>("testimonials", t);
    },
  };

  roles = {
    getAll: async (): Promise<CustomRole[]> => getAll<CustomRole>("roles"),
    saveAll: async (roles: CustomRole[]) => {
      await replaceAll<CustomRole>("roles", roles);
    },
  };

  settings = {
    getTheme: async (): Promise<"light" | "dark"> => {
      const r = await api<{ theme: "light" | "dark" }>("/api/settings/theme");
      return r.theme;
    },
    setTheme: async (theme: "light" | "dark") => {
      await api("/api/settings/theme", {
        method: "PUT",
        body: JSON.stringify({ theme }),
      });
    },
  };
}

export const db = new MongoBackedDB();
